import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/auth/sso/status').then(r => setSsoEnabled(r.data.enabled)).catch(() => {});
    // Show SSO error from query param if redirected back
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err) setError(decodeURIComponent(err));
  }, []);

  const handleSso = () => {
    setSsoLoading(true);
    window.location.href = '/api/auth/sso/login';
  };

  const handleSubmit = async e => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login(email, password); navigate('/'); }
    catch (err) { setError(err.response?.data?.error || 'Login failed'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'linear-gradient(135deg,#0f172a 0%,#1e3a5f 60%,#0f172a 100%)',padding:20,position:'relative' }}>
      <div style={{ position:'absolute',inset:0,backgroundImage:'radial-gradient(circle at 20% 50%,rgba(37,99,235,0.18) 0%,transparent 50%),radial-gradient(circle at 80% 20%,rgba(16,185,129,0.12) 0%,transparent 50%)',pointerEvents:'none' }} />
      <div style={{ background:'white',borderRadius:16,padding:'44px 40px',width:'100%',maxWidth:400,boxShadow:'0 25px 60px rgba(0,0,0,0.4)',position:'relative',zIndex:1 }}>
        <div style={{ textAlign:'center',marginBottom:28 }}>
          <div style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',gap:10,marginBottom:10 }}>
            <span style={{ fontSize:38,lineHeight:1 }}>⚡</span>
            <span style={{ fontSize:34,fontWeight:900,color:'#0f172a',letterSpacing:'-1px' }}>Pulse</span>
          </div>
          <div style={{ fontSize:13.5,color:'#64748b',letterSpacing:'0.2px' }}>Sales & Contracts Platform</div>
          <div style={{ fontSize:11.5,color:'#94a3b8',marginTop:4 }}>by iShift.io</div>
          <div style={{ width:44,height:3,background:'linear-gradient(90deg,#2563eb,#10b981)',borderRadius:3,margin:'14px auto 0' }} />
        </div>

        {error && <div style={{ background:'#fee2e2',color:'#dc2626',padding:'10px 14px',borderRadius:8,fontSize:13,marginBottom:16 }}>{error}</div>}

        {/* M365 SSO Button */}
        {ssoEnabled && (
          <>
            <button
              onClick={handleSso}
              disabled={ssoLoading}
              style={{ width:'100%',padding:'11px',background:'#fff',color:'#1d2939',border:'1.5px solid #d1d5db',borderRadius:8,fontSize:14,fontWeight:600,cursor:ssoLoading?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:10,marginBottom:16,transition:'border-color 0.15s,box-shadow 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='#2563eb'}
              onMouseLeave={e => e.currentTarget.style.borderColor='#d1d5db'}
            >
              {/* Microsoft logo */}
              <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="0" width="10" height="10" fill="#f25022"/>
                <rect x="11" y="0" width="10" height="10" fill="#7fba00"/>
                <rect x="0" y="11" width="10" height="10" fill="#00a4ef"/>
                <rect x="11" y="11" width="10" height="10" fill="#ffb900"/>
              </svg>
              {ssoLoading ? 'Redirecting...' : 'Sign in with Microsoft 365'}
            </button>
            <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:16 }}>
              <div style={{ flex:1,height:1,background:'#e5e7eb' }}/>
              <span style={{ fontSize:12,color:'#9ca3af' }}>or use local account</span>
              <div style={{ flex:1,height:1,background:'#e5e7eb' }}/>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block',fontSize:13,fontWeight:500,color:'#374151',marginBottom:5 }}>Email Address</label>
            <input style={{ width:'100%',padding:'10px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,outline:'none',boxSizing:'border-box',transition:'border-color 0.15s' }}
              type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@ishift.io" required={!ssoEnabled} autoFocus
              onFocus={e=>e.target.style.borderColor='#2563eb'} onBlur={e=>e.target.style.borderColor='#d1d5db'} />
          </div>
          <div style={{ marginBottom:22 }}>
            <label style={{ display:'block',fontSize:13,fontWeight:500,color:'#374151',marginBottom:5 }}>Password</label>
            <input style={{ width:'100%',padding:'10px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,outline:'none',boxSizing:'border-box',transition:'border-color 0.15s' }}
              type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required={!ssoEnabled}
              onFocus={e=>e.target.style.borderColor='#2563eb'} onBlur={e=>e.target.style.borderColor='#d1d5db'} />
          </div>
          <button style={{ width:'100%',padding:'12px',background:'#2563eb',color:'white',border:'none',borderRadius:8,fontSize:14,fontWeight:600,cursor:loading?'not-allowed':'pointer',opacity:loading?0.7:1,transition:'background 0.15s' }}
            type="submit" disabled={loading}
            onMouseEnter={e=>!loading&&(e.target.style.background='#1d4ed8')}
            onMouseLeave={e=>!loading&&(e.target.style.background='#2563eb')}>
            {loading?'Signing in...':'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
